import { createContext, type ReactNode, useContext } from 'react';

import { ZackeyAgentABT, ZackeyAgentFeatureToggle } from '../constants/abt';

/**
 * 재키 진입 어댑터 입력 파라미터
 * - catalogProductId: 필수. 현재 PDP 상품 ID
 * - sessionId: 선택. 기존 세션으로 재진입 시 사용
 */
export interface ZackeyAgentEntryParams {
  catalogProductId: string;
  sessionId?: string;
}

/**
 * 재키 진입 어댑터 인터페이스
 * 앱/서비스 레이어에서 구체적인 진입 방식을 주입할 때 사용
 */
export interface ZackeyAgentEntryAdapter {
  openZackeyAgent: (params: ZackeyAgentEntryParams) => void;
}

interface ZackeyAgentPDPEntryContextType {
  openZackeyAgent: (params: ZackeyAgentEntryParams) => void;
}

const ZackeyAgentPDPEntryContext = createContext<ZackeyAgentPDPEntryContextType | null>(null);

export interface ZackeyAgentPDPEntryProviderProps {
  /** ABT feature_list 조회에 사용될 사용자 식별자 (ZIGZAGUUID) */
  zigzagUuid: string | null | undefined;
  /** 재키 진입 어댑터. 미주입 시 no-op (개발 환경에서는 console.warn) */
  entryAdapter?: ZackeyAgentEntryAdapter;
  children: ReactNode;
}

/**
 * ZackeyAgentPDPEntryProvider에서 제공하는 openZackeyAgent 함수를 사용하기 위한 훅
 * 반드시 ZackeyAgentPDPEntryProvider 하위에서 호출해야 함
 */
export const useZackeyAgentPDPEntryContext = () => {
  const context = useContext(ZackeyAgentPDPEntryContext);
  if (!context) {
    throw new Error('useZackeyAgentPDPEntryContext must be used within a ZackeyAgentPDPEntryProvider');
  }
  return context;
};

/**
 * 재키(zackey-agent) 관련 전역 wiring을 담당하는 Provider
 *
 * - ABT Provider를 렌더링하여 하위에서 ABT 컨텍스트 훅을 사용할 수 있게 함
 * - FeatureToggle Provider를 렌더링하여 하위에서 FeatureToggle 컨텍스트 훅을 사용할 수 있게 함
 * - 진입 어댑터를 컨텍스트로 제공하여 버튼 클릭 시 진입 동작을 위임
 *
 * @example
 * ```tsx
 * <ZackeyAgentPDPEntryProvider zigzagUuid={zigzagUuid}>
 *   <ProductDetailScreen />
 * </ZackeyAgentPDPEntryProvider>
 * ```
 */
export const ZackeyAgentPDPEntryProvider = (props: ZackeyAgentPDPEntryProviderProps) => {
  const { zigzagUuid, entryAdapter, children } = props;

  const openZackeyAgent = (params: ZackeyAgentEntryParams) => {
    if (entryAdapter) {
      entryAdapter.openZackeyAgent(params);
      return;
    }

    if (process.env.NODE_ENV === 'production') {
      return;
    }

    console.warn(
      '[ZackeyAgentPDPEntryProvider] entryAdapter가 주입되지 않았습니다. 진입 동작을 수행하려면 entryAdapter를 전달해주세요.',
    );
  };

  return (
    <ZackeyAgentABT.ABTProvider zigzagUuid={zigzagUuid}>
      <ZackeyAgentFeatureToggle.ABTProvider zigzagUuid={zigzagUuid}>
        <ZackeyAgentPDPEntryContext.Provider value={{ openZackeyAgent }}>
          {children}
        </ZackeyAgentPDPEntryContext.Provider>
      </ZackeyAgentFeatureToggle.ABTProvider>
    </ZackeyAgentABT.ABTProvider>
  );
};
